import { ArrowRight, Play, User, Wand2, Settings, Package, Zap, BookOpen, Plus, Share2, Calendar } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"

/**
 * Agent Builder Mockup component that visually represents the actual agent builder interface.
 * This is a non-functional visual representation used for marketing purposes.
 */
function AgentBuilderMockup() {
  return (
    <div className="w-full rounded-xl border border-neutral-700 bg-neutral-900 shadow-2xl overflow-hidden">
      {/* Header Bar */}
      <div className="flex items-center justify-between border-b border-neutral-800 px-4 py-2.5">
        <div className="flex items-center gap-3">
          <div className="h-6 w-6 rounded bg-neutral-800 flex items-center justify-center">
            <ArrowRight className="h-3 w-3 rotate-180 text-neutral-500" />
          </div>
          <span className="text-sm font-medium text-white">Sales Assistant</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded bg-neutral-800 px-2.5 py-1 text-xs text-neutral-400 flex items-center gap-1">
            <Play className="h-3 w-3" />
            Test Agent
          </div>
          <div className="rounded bg-secondary/90 px-2.5 py-1 text-xs text-white flex items-center gap-1">
            <Share2 className="h-3 w-3" />
            Publish & Share
          </div>
          <div className="rounded bg-primary px-2.5 py-1 text-xs text-white">
            Save Agent
          </div>
        </div>
      </div>

      {/* 3-Column Layout */}
      <div className="grid grid-cols-12 min-h-[320px]">
        {/* Left Column - Profile Section */}
        <div className="col-span-5 border-r border-neutral-800 p-4">
          {/* Avatar and Name */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary/30 to-primary/10">
                <User className="h-6 w-6 text-primary" />
              </div>
            </div>
            <div className="flex-1">
              <div className="text-xs text-neutral-500 mb-1">Agent Name *</div>
              <div className="h-8 rounded border border-neutral-700 bg-neutral-800 px-2.5 flex items-center">
                <span className="text-sm text-white">Sales Assistant</span>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="mb-4">
            <div className="text-xs text-neutral-500 mb-1">Description *</div>
            <div className="relative h-16 rounded border border-neutral-700 bg-neutral-800 p-2.5">
              <div className="space-y-1">
                <div className="h-2 w-full rounded bg-neutral-700" />
                <div className="h-2 w-3/4 rounded bg-neutral-700" />
                <div className="h-2 w-5/6 rounded bg-neutral-700/50" />
              </div>
              <div className="absolute bottom-1.5 right-1.5">
                <Wand2 className="h-3.5 w-3.5 text-neutral-500" />
              </div>
            </div>
          </div>

          {/* System Instructions */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-neutral-500">System Instructions</span>
              <div className="flex items-center gap-1 text-xs text-neutral-500">
                <Wand2 className="h-3 w-3" />
                <span>Enhance</span>
              </div>
            </div>
            <div className="rounded border border-neutral-700 bg-neutral-950 p-3 font-mono text-xs">
              <div className="text-purple-400 mb-1">You are a helpful...</div>
              <div className="text-neutral-500 mb-1"># Guidelines</div>
              <div className="text-neutral-400">- Be concise</div>
              <div className="text-neutral-400">- Use markdown</div>
            </div>
          </div>
        </div>

        {/* Middle Column - Capabilities */}
        <div className="col-span-3 border-r border-neutral-800">
          {/* Model Selector */}
          <div className="flex items-center gap-2 border-b border-neutral-800 px-3 py-2.5">
            <div className="flex-1 rounded border border-neutral-700 bg-neutral-800 px-2.5 py-1.5 text-xs text-white flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-emerald-500/20 flex items-center justify-center">
                <span className="text-xs text-emerald-400">AI</span>
              </div>
              GPT-4o
            </div>
            <Settings className="h-4 w-4 text-neutral-500" />
          </div>

          {/* Features */}
          <div className="space-y-0">
            {[
              { label: "Attachments", enabled: true },
              { label: "Text-to-Speech", enabled: true },
              { label: "Web Search", enabled: false },
              { label: "Follow-up", enabled: true },
            ].map((feature, i) => (
              <div
                key={i}
                className="flex items-center justify-between border-b border-neutral-800 px-3 py-2.5"
              >
                <span className="text-xs text-neutral-300">{feature.label}</span>
                <Switch checked={feature.enabled} className="scale-75" disabled />
              </div>
            ))}
          </div>

          {/* Tools Header */}
          <div className="border-b border-neutral-800 px-3 py-2 bg-neutral-800/50">
            <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Built-in Tools
            </span>
          </div>

          {/* Tools */}
          <div className="space-y-0">
            {["Terminal", "File Manager", "Code Execution"].map((tool, i) => (
              <div
                key={i}
                className="flex items-center justify-between border-b border-neutral-800 px-3 py-2.5"
              >
                <span className="text-xs text-neutral-300">{tool}</span>
                <Switch checked={i < 2} className="scale-75" disabled />
              </div>
            ))}
          </div>
        </div>

        {/* Right Column - Connections */}
        <div className="col-span-4 p-4 space-y-4">
          {/* Integrations */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-xs text-neutral-300">
                <Package className="h-3.5 w-3.5" />
                Integrations
              </div>
              <div className="flex items-center gap-1 text-xs text-primary">
                <Plus className="h-3 w-3" />
                Add
              </div>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {["Slack", "Gmail", "Jira", "GitHub"].map((app, i) => (
                <div
                  key={i}
                  className="flex h-8 items-center justify-center rounded border border-neutral-700 bg-neutral-800 text-xs text-neutral-400"
                >
                  {app}
                </div>
              ))}
            </div>
          </div>

          {/* Triggers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-xs text-neutral-300">
                <Zap className="h-3.5 w-3.5" />
                Triggers
              </div>
              <div className="flex items-center gap-1 text-xs text-primary">
                <Plus className="h-3 w-3" />
                Add
              </div>
            </div>
            <div className="rounded border border-neutral-700 bg-neutral-800 p-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-neutral-300">On new email</span>
                <span className="rounded bg-primary/20 px-1.5 py-0.5 text-xs text-primary">
                  Gmail
                </span>
              </div>
            </div>
          </div>

          {/* Knowledge */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-xs text-neutral-300">
                <BookOpen className="h-3.5 w-3.5" />
                Knowledge
              </div>
              <div className="flex items-center gap-1 text-xs text-primary">
                <Plus className="h-3 w-3" />
                Add
              </div>
            </div>
            <div className="rounded border border-primary/30 bg-primary/5 p-2.5">
              <div className="flex items-center gap-2">
                <div className="rounded bg-neutral-800 p-1.5">
                  <BookOpen className="h-3 w-3 text-neutral-400" />
                </div>
                <div>
                  <div className="text-xs text-neutral-300">Company Docs</div>
                  <div className="text-xs text-neutral-500">1,234 chunks</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Hero section for the Agent Studio product page.
 * Features a dark gradient background with a mockup of the agent builder.
 */
export function HeroSection() {
  const navigate = useNavigate()

  return (
    <section className="relative overflow-hidden bg-neutral-900 pt-24 pb-16 lg:pt-32 lg:pb-24">
      {/* Background decoration */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:32px_32px]" />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Text Content - Centered */}
        <div className="mb-12 text-center">
          <Badge className="mb-6 border-primary/30 bg-primary/10 text-primary">
            Agent Studio
          </Badge>
          <h1 className="mb-6 text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
            Build Powerful AI Agents{" "}
            <span className="text-primary">in Minutes</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-neutral-400">
            Create, customize, and deploy intelligent AI agents with our
            intuitive visual builder. No coding required. Just point, click,
            and let your agents work.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              className="group bg-primary text-white hover:bg-primary/90"
              onClick={() => navigate("/signup")}
            >
              Start Building
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-neutral-700 bg-transparent text-white hover:bg-neutral-800"
              onClick={() => navigate("/demo")}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Book a Demo
            </Button>
          </div>
        </div>

        {/* Agent Builder Mockup */}
        <div className="relative">
          <AgentBuilderMockup />
          {/* Decorative glow */}
          <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-r from-primary/20 via-transparent to-secondary/20 opacity-50 blur-2xl" />
        </div>
      </div>
    </section>
  )
}
